/**
 * Lo que Gavi recuerda de los pagos fijos (pref sincronizada
 * `pagos-fijos-gavi`, JSON): lo que se le dijo «No», el contrato de cada
 * pago fijo, los consejos silenciados o por recordar, las respuestas a sus
 * preguntas y los recibos dados de baja. Funciones puras.
 */
import { esTipoRecibo } from './tiposRecibo';
import type { TipoRecibo } from './tiposRecibo';

/** Tarjeta «Contrato» de un pago fijo (fechas `YYYY-MM-DD`). */
export type Contrato = {
  tipo?: TipoRecibo;
  renueva?: string;
  permanencia?: string;
  finPromo?: string;
  /** La persona ha dicho «Sí» a lo que Gavi sacó del historial. */
  confirmado?: boolean;
};

export type Respuesta = 'si' | 'no' | 'nose';

export type EstadoGavi = {
  /** Series a las que se dijo «No» en «He encontrado…». */
  descartados: string[];
  /**
   * Por recibo (`categoría|serie`). Los antiguos, solo por categoría: son
   * del primer recibo de esa categoría.
   */
  contratos: Record<string, Contrato>;
  /** Clave de consejo → hasta cuándo calla (`YYYY-MM-DD`). */
  silencios: Record<string, string>;
  /** Clave de consejo → desde cuándo vuelve, como aviso. */
  recordar: Record<string, string>;
  /** Clave de pregunta → respuesta. */
  respuestas: Record<string, Respuesta>;
  /** Clave de serie → día en que se dio de baja. */
  bajas: Record<string, string>;
};

export const ESTADO_VACIO: EstadoGavi = {
  descartados: [],
  contratos: {},
  silencios: {},
  recordar: {},
  respuestas: {},
  bajas: {},
};

/** Tope de entradas por lista (no crece sin fin). */
const MAX = 200;

const FECHA = /^\d{4}-\d{2}-\d{2}$/;

function esObjeto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function fechas(v: unknown): Record<string, string> {
  const r: Record<string, string> = {};
  if (esObjeto(v)) {
    for (const [k, f] of Object.entries(v)) {
      if (typeof f === 'string' && FECHA.test(f)) {
        r[k] = f;
      }
    }
  }
  return r;
}

function contrato(v: unknown): Contrato | null {
  if (!esObjeto(v)) {
    return null;
  }
  const c: Contrato = {};
  if (esTipoRecibo(v.tipo)) {
    c.tipo = v.tipo;
  }
  for (const campo of ['renueva', 'permanencia', 'finPromo'] as const) {
    const f = v[campo];
    if (typeof f === 'string' && FECHA.test(f)) {
      c[campo] = f;
    }
  }
  if (v.confirmado === true) {
    c.confirmado = true;
  }
  return c;
}

/** Lee la pref; lo ilegible se ignora (nunca rompe la pantalla). */
export function leerEstadoGavi(raw: string | null | undefined): EstadoGavi {
  if (!raw) {
    return ESTADO_VACIO;
  }
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return ESTADO_VACIO;
  }
  if (!esObjeto(v)) {
    return ESTADO_VACIO;
  }
  const contratos: Record<string, Contrato> = {};
  if (esObjeto(v.contratos)) {
    for (const [id, c] of Object.entries(v.contratos)) {
      const leido = contrato(c);
      if (leido) {
        contratos[id] = leido;
      }
    }
  }
  const respuestas: Record<string, Respuesta> = {};
  if (esObjeto(v.respuestas)) {
    for (const [k, r] of Object.entries(v.respuestas)) {
      if (r === 'si' || r === 'no' || r === 'nose') {
        respuestas[k] = r;
      }
    }
  }
  return {
    descartados: Array.isArray(v.descartados)
      ? v.descartados.filter((d): d is string => typeof d === 'string')
      : [],
    contratos,
    silencios: fechas(v.silencios),
    recordar: fechas(v.recordar),
    respuestas,
    bajas: fechas(v.bajas),
  };
}

/** Deja solo las `MAX` últimas entradas de un registro. */
function recortar<T>(r: Record<string, T>): Record<string, T> {
  const entradas = Object.entries(r);
  return entradas.length <= MAX
    ? r
    : Object.fromEntries(entradas.slice(entradas.length - MAX));
}

export function escribirEstadoGavi(e: EstadoGavi): string {
  return JSON.stringify({
    descartados: e.descartados.slice(-MAX),
    contratos: e.contratos,
    silencios: recortar(e.silencios),
    recordar: recortar(e.recordar),
    respuestas: recortar(e.respuestas),
    bajas: recortar(e.bajas),
  });
}

export function conDescartado(e: EstadoGavi, clave: string): EstadoGavi {
  return e.descartados.includes(clave)
    ? e
    : { ...e, descartados: [...e.descartados, clave] };
}

export function conContrato(
  e: EstadoGavi,
  categoria: string,
  cambios: Partial<Contrato>,
): EstadoGavi {
  const actual = { ...e.contratos[categoria], ...cambios };
  for (const k of Object.keys(actual) as (keyof Contrato)[]) {
    if (actual[k] === undefined) {
      delete actual[k];
    }
  }
  return { ...e, contratos: { ...e.contratos, [categoria]: actual } };
}

function claveContrato(categoria: string, serie: string): string {
  return `${categoria}|${serie}`;
}

/**
 * El contrato del recibo `i` de una categoría (`series`: sus claves, la de
 * más dinero primero). El primero hereda el que se guardaba por categoría.
 */
export function contratoDe(
  e: EstadoGavi,
  categoria: string,
  series: readonly string[],
  i = 0,
): Contrato | undefined {
  const propio =
    series[i] != null
      ? e.contratos[claveContrato(categoria, series[i])]
      : undefined;
  return propio ?? (i === 0 ? e.contratos[categoria] : undefined);
}

/**
 * Cambia el contrato del recibo `i`. Si quedaba uno guardado por categoría,
 * pasa antes al primer recibo, para que no salte a otro si cambia el orden.
 */
export function conContratoDe(
  e: EstadoGavi,
  categoria: string,
  series: readonly string[],
  i: number,
  cambios: Partial<Contrato>,
): EstadoGavi {
  let r = e;
  const antiguo = e.contratos[categoria];
  if (antiguo && series[0] != null) {
    const { [categoria]: _quitado, ...resto } = e.contratos;
    const primero = claveContrato(categoria, series[0]);
    r = {
      ...e,
      contratos: { ...resto, [primero]: resto[primero] ?? antiguo },
    };
  }
  return conContrato(r, claveContrato(categoria, series[i]), cambios);
}

/** Calla un consejo hasta `hasta` (y deja de estar «por recordar»). */
export function conSilencio(
  e: EstadoGavi,
  clave: string,
  hasta: string,
): EstadoGavi {
  const { [clave]: _, ...recordar } = e.recordar;
  return { ...e, silencios: { ...e.silencios, [clave]: hasta }, recordar };
}

/** «Recuérdamelo»: calla hasta `cuando` y entonces vuelve como aviso. */
export function conRecordar(
  e: EstadoGavi,
  clave: string,
  cuando: string,
): EstadoGavi {
  return {
    ...e,
    silencios: { ...e.silencios, [clave]: cuando },
    recordar: { ...e.recordar, [clave]: cuando },
  };
}

export function conRespuesta(
  e: EstadoGavi,
  clave: string,
  respuesta: Respuesta,
): EstadoGavi {
  return { ...e, respuestas: { ...e.respuestas, [clave]: respuesta } };
}

/** «Lo he dado de baja» (o `null` para deshacerlo). */
export function conBaja(
  e: EstadoGavi,
  serie: string,
  fecha: string | null,
): EstadoGavi {
  const { [serie]: _, ...resto } = e.bajas;
  return { ...e, bajas: fecha ? { ...resto, [serie]: fecha } : resto };
}
